import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import type {
  ServiceWithOperator,
  ServiceVisitType,
  NewServiceWithOperator,
  RulesConfigResponse,
  UpdateRulesConfigPayload,
  UpdateServiceWithOperatorPayload,
} from '../models';
import { API_BUSINESS_URL } from '@/utils';

const serviceWithOperatorService = createApi({
  reducerPath: 'serviceWithOperatorService',
  baseQuery: createBaseQueryWithReauth(API_BUSINESS_URL),
  tagTypes: [
    'ServiceWithOperator',
    'ServiceWithOperators',
    'RulesConfig',
    'ServiceVisitTypes',
  ],
  endpoints: (builder) => ({
    getServiceVisitTypes: builder.query<ServiceVisitType[], void>({
      query: () => ({ url: '/service-visit-types' }),
      transformResponse: (
        response:
          | ServiceVisitType[]
          | { data?: ServiceVisitType[]; items?: ServiceVisitType[] }
      ) =>
        Array.isArray(response)
          ? response
          : (response?.data ?? response?.items ?? []),
      providesTags: ['ServiceVisitTypes'],
    }),
    getServiceWithOperators: builder.query<
      PaginatedResponse<ServiceWithOperator>,
      {
        paginated: PaginatedRequest;
        logisticOperatorId: string;
        filters?: {
          name?: string;
          commercialServiceId?: string;
        };
      }
    >({
      query: ({ paginated, logisticOperatorId, filters }) => ({
        url: `/logistic-operator/${logisticOperatorId}/services`,
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters?.name &&
            filters.name.trim() !== '' && {
              name: filters.name.trim(),
            }),
          ...(filters?.commercialServiceId &&
            filters.commercialServiceId.trim() !== '' && {
              commercialServiceId: filters.commercialServiceId.trim(),
            }),
        },
      }),
      providesTags: (result, _error, { logisticOperatorId }) => {
        const baseTags = [
          ...(result?.items?.map(({ id }) => ({
            type: 'ServiceWithOperator' as const,
            id,
          })) ?? []),
          { type: 'ServiceWithOperators' as const, id: logisticOperatorId },
        ];

        return [...baseTags, 'ServiceWithOperators'];
      },
    }),
    searchServiceWithOperators: builder.query<
      PaginatedResponse<ServiceWithOperator>,
      {
        paginated: PaginatedRequest;
        logisticOperatorId?: string;
        name?: string;
        commercialServiceId?: string;
        includeAncestors?: boolean;
      }
    >({
      query: ({
        paginated,
        logisticOperatorId,
        name,
        commercialServiceId,
        includeAncestors,
      }) => ({
        url: '/service-with-operator',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(logisticOperatorId?.trim() && {
            logisticOperatorId: logisticOperatorId.trim(),
          }),
          ...(includeAncestors && { includeAncestors: true }),
          ...(name?.trim() && { name: name.trim() }),
          ...(commercialServiceId?.trim() && {
            commercialServiceId: commercialServiceId.trim(),
          }),
        },
      }),
      providesTags: ['ServiceWithOperators'],
    }),
    getServiceWithOperatorById: builder.query<
      ServiceWithOperator,
      { serviceWithOperatorId: string }
    >({
      query: ({ serviceWithOperatorId }) => ({
        url: `/service-with-operator/${serviceWithOperatorId}`,
      }),
      providesTags: ['ServiceWithOperator'],
    }),
    createServiceWithOperator: builder.mutation<
      ServiceWithOperator,
      { logisticOperatorId: string; body: NewServiceWithOperator }
    >({
      query: ({ logisticOperatorId, body }) => ({
        url: `/logistic-operator/${logisticOperatorId}/services`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { logisticOperatorId }) => [
        { type: 'ServiceWithOperators', id: logisticOperatorId },
        'ServiceWithOperators',
      ],
    }),
    deleteServiceWithOperator: builder.mutation<
      void,
      { serviceWithOperatorId: string; logisticOperatorId?: string }
    >({
      query: ({ serviceWithOperatorId }) => ({
        url: `/service-with-operator/${serviceWithOperatorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (
        _result,
        _error,
        { serviceWithOperatorId, logisticOperatorId }
      ) => [
        { type: 'ServiceWithOperator', id: serviceWithOperatorId },
        ...(logisticOperatorId
          ? [{ type: 'ServiceWithOperators' as const, id: logisticOperatorId }]
          : []),
        'ServiceWithOperators',
      ],
    }),
    getRulesConfigById: builder.query<
      RulesConfigResponse,
      { serviceWithOperatorId: string }
    >({
      query: ({ serviceWithOperatorId }) => ({
        url: `/service-with-operator/${serviceWithOperatorId}/rules-config`,
      }),
      providesTags: (_result, _error, { serviceWithOperatorId }) => [
        { type: 'RulesConfig', id: serviceWithOperatorId },
      ],
    }),
    updateRulesConfig: builder.mutation<
      RulesConfigResponse,
      { serviceWithOperatorId: string; body: UpdateRulesConfigPayload }
    >({
      query: ({ serviceWithOperatorId, body }) => ({
        url: `/service-with-operator/${serviceWithOperatorId}/rules-config`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { serviceWithOperatorId }) => [
        { type: 'RulesConfig', id: serviceWithOperatorId },
        'ServiceWithOperator',
        'ServiceWithOperators',
      ],
    }),
    updateServiceWithOperator: builder.mutation<
      ServiceWithOperator,
      {
        logisticOperatorId: string;
        serviceWithOperatorId: string;
        body: UpdateServiceWithOperatorPayload;
      }
    >({
      query: ({ logisticOperatorId, serviceWithOperatorId, body }) => ({
        url: `/logistic-operator/${logisticOperatorId}/services/${serviceWithOperatorId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (
        _result,
        _error,
        { serviceWithOperatorId, logisticOperatorId }
      ) => [
        { type: 'ServiceWithOperator', id: serviceWithOperatorId },
        { type: 'ServiceWithOperators', id: logisticOperatorId },
        'ServiceWithOperator',
        'ServiceWithOperators',
      ],
    }),
  }),
});

export default serviceWithOperatorService;
export const {
  useGetServiceVisitTypesQuery,
  useGetServiceWithOperatorsQuery,
  useSearchServiceWithOperatorsQuery,
  useGetServiceWithOperatorByIdQuery,
  useCreateServiceWithOperatorMutation,
  useDeleteServiceWithOperatorMutation,
  useGetRulesConfigByIdQuery,
  useUpdateRulesConfigMutation,
  useUpdateServiceWithOperatorMutation,
} = serviceWithOperatorService;
